import * as assert from 'assert';
import ExifReader, { ExifDateAttribute } from 'exifreader';
import * as moment from 'moment';
import * as React from 'react';
import Dropzone, { ImageFile } from 'react-dropzone';
import { RouteComponentProps } from 'react-router';
import { clone } from 'underscore';
import {
    AddPhotoConstants as ui,
    ACCEPT_IMAGE_TYPE,
    FRESHDESK,
    MAX_PHOTOS_PER_UPLOAD,
    PHOTO_DATE_FORMAT
} from '../../Config/UIConstants';
import { TagType } from '../../Services/PhotoService';
import { CommonActionButton } from '../../SharedComponents/CommonActionButton';
import { DuplicatePhotoPrompt } from '../../SharedComponents/DuplicatePhotoPrompt';
import { ExitableAlert } from '../../SharedComponents/ExitableAlert';
import { FullContainer } from '../../SharedComponents/FullContainer';
import { InformationIcon } from '../../SharedComponents/InformationIcon';
import { IUploadedPhoto } from '../../Stores/UserPhotoStore';
import { ApplicationStore } from '../../_stores/ApplicationStore';
import { UploadPhotoIcon } from '../../images/images';
import { compressImage, getNextRotation, getRotatedFileFromImage } from '../../util/resizeImage';
import { styles } from '../PhotosPage/AddPhotosStyle';
import { IPossibleDuplicate, ISimplePhotoData } from '../StoriesPage/StoryPhotoUploader';
import { PhotoDataInput } from './PhotoDataInput';

import { Errors } from '../../SharedComponents/Errors/Errors';

interface IState {
    images: Array<IUploadedPhoto>;
    currentImageFocusIndex: number;
    indexOfImageToUpload: number;
    showUploadProgress: boolean;
    showExitConfirm?: boolean;
    showExtraPhotoNumber?: number;
    possibleDuplicates: Array<IPossibleDuplicate>;
    duplicateIndex: number;
    errors: Array<string>;
    showUploadErrorConfirmButton: boolean;
}

interface IProps extends RouteComponentProps<any> {
    store: ApplicationStore;
}

export abstract class AbstractAddPhotosPage<P extends IProps> extends React.Component<P, IState> {
    public state: IState = {
        images: [],
        currentImageFocusIndex: 0,
        indexOfImageToUpload: 0,
        showUploadProgress: false,
        showExitConfirm: false,
        possibleDuplicates: [],
        duplicateIndex: 0,
        errors: [],
        showUploadErrorConfirmButton: false,
    };

    protected dropzoneRef: any;

    protected async onDrop(files: Array<ImageFile>): Promise<void> {
        console.assert(files, 'Parameter is undefined');
        const filesWithInfo = await Exif.getUpdatedImageFiles(files);

        this.setState({errors: []});

        filesWithInfo.forEach((file, index) => {
            if (file.error) {
                let error = `${file.filename} can not be uploaded: ${file.error}`;
                this.setState({errors: [...this.state.errors, error]});
            }
        });

        if (this.state.images.length + filesWithInfo.length > MAX_PHOTOS_PER_UPLOAD) {
            alert(ui.MAX_UPLOAD_LIMIT);
            return;
        }

        const { uniquePhotos, possibleDuplicates } = await this.checkForDuplicateFileNames(
            filesWithInfo,
        );
        this.setState({
            images: [...this.state.images, ...uniquePhotos.filter(file => !file.error)],
            possibleDuplicates
        });
    }

    private onClickDuplicate = (isDuplicate: boolean) => {
        let index = this.state.duplicateIndex;
        const duplicates = [...this.state.possibleDuplicates];

        duplicates[index].isDuplicate = isDuplicate;

        index += 1;

        if (index >= duplicates.length) {
            const uniqueFiles = this.getUniqueFilesForDuplicates(duplicates);

            this.setState({
                images: [...this.state.images, ...uniqueFiles],
                possibleDuplicates: [],
                duplicateIndex: 0,
            });

        } else {
            this.setState({ possibleDuplicates: duplicates, duplicateIndex: index });
        }
    }

    private getUniqueFilesForDuplicates(duplicates: Array<IPossibleDuplicate>): Array<IUploadedPhoto> {
        const possibleDuplicateGroupedByFile: { [index: number]: Array<IPossibleDuplicate> } = {};
        duplicates.forEach(d => {
            if (!possibleDuplicateGroupedByFile[d.tempID]) {
                possibleDuplicateGroupedByFile[d.tempID] = [d];
            } else {
                possibleDuplicateGroupedByFile[d.tempID].push(d);
            }
        });

        const uniqueFiles: Array<IUploadedPhoto> = [];
        Object.keys(possibleDuplicateGroupedByFile).map(key => {
            const groupContainsDuplicate = possibleDuplicateGroupedByFile[parseInt(key)].some(
                (x: IPossibleDuplicate) => x.isDuplicate,
            );
            if (!groupContainsDuplicate && possibleDuplicateGroupedByFile[parseInt(key)].length !== 0) {
                uniqueFiles.push(possibleDuplicateGroupedByFile[parseInt(key)][0].newPhoto);
            }
        });

        return uniqueFiles;
    }

    private renderDuplicatePrompt() {
        const itemToCheck = this.state.possibleDuplicates[this.state.duplicateIndex];
        return (
            <DuplicatePhotoPrompt
                fileName={itemToCheck.newPhoto.filename}
                newPhotoUrl={itemToCheck.newPhoto.image!.preview}
                matchingPhotoUrl={itemToCheck.originalPhoto.url}
                onClickIsDuplicate={() => this.onClickDuplicate(true)}
                onClickFalseDuplicate={() => this.onClickDuplicate(false)}
            />
        );
    }

    private async checkForDuplicateFileNames(
        uploadedFiles: Array<IUploadedPhoto>,
    ): Promise<{ uniquePhotos: Array<IUploadedPhoto>; possibleDuplicates: Array<IPossibleDuplicate> }> {

        await this.props.store.userPhotoStore.checkForDuplicatePhotos(uploadedFiles);
        const uniquePhotos: Array<IUploadedPhoto> = [];
        let possibleDuplicates: Array<IPossibleDuplicate> = [];
        const currentPhotos = this.getAllCurrentFileNamesAndUrls();

        uploadedFiles.forEach((newPhoto, index) => {
            const userPhotosWithSameFileName = currentPhotos.filter(p => p.name === newPhoto.filename);
            if (userPhotosWithSameFileName.length > 0) {
                const newPossibleDuplicates = userPhotosWithSameFileName.map(p => {
                    return { originalPhoto: p, newPhoto, tempID: index, isDuplicate: undefined };
                });
                possibleDuplicates = [...possibleDuplicates, ...newPossibleDuplicates];
            } else {
                uniquePhotos.push(newPhoto);
            }
        });

        return { uniquePhotos, possibleDuplicates };
    }

    private getAllCurrentFileNamesAndUrls(): Array<ISimplePhotoData> {
        const possibleDupes = [...this.props.store!.userPhotoStore.possibleDupes].map(p => {
            return { name: p.description.filename, url: p.url };
        });

        const queuedPhotos = [...this.state.images].map(i => {
            return { name: i.filename, url: i.image!.preview };
        });
        return [...possibleDupes, ...queuedPhotos];
    }

    protected removeCurrentImage(): void {
        const state = [...this.state.images];
        state.splice(this.state.currentImageFocusIndex, 1);
        this.setState({ images: state, currentImageFocusIndex: Math.max(this.state.currentImageFocusIndex - 1, 0) });
    }

    // TODO: THUMBNAIL = Put the 450 in a constant (in this file). THUMBNAIL_WIDTH_PIXELS?
    protected uploadAllImages = async (): Promise<void> => {
        if (this.state.indexOfImageToUpload === 0) {
            if (this.state.showUploadProgress) {
                assert(false, 'uploadAllImages() was called twice at the same time..');
                return;
            }
            this.setState({ showUploadProgress: true });
        }
        
        if (this.state.indexOfImageToUpload === this.state.images.length) {

            if (this.props.store.userPhotoStore && this.props.store.userPhotoStore.initialPhotosAreLoaded) {
                const clonedMeta = await clone(this.props.store.userPhotoStore.getAllPhotosForCurrentUserMeta);
                clonedMeta.page = 0;
                await this.props.store.userPhotoStore.setGetAllPhotosForCurrentUserMeta(clonedMeta);
            }

            if (this.state.errors.length > 0) {
                this.setState({showUploadErrorConfirmButton: true});
                return;
            }

            await this.onFinishedUploadedPhotosCallback();
            this.setState({ showUploadProgress: false });
            return;
        }

        const indexToUpload = this.state.indexOfImageToUpload;
        this.setState({ indexOfImageToUpload: this.state.indexOfImageToUpload + 1 });

        let imgSource = { ...this.state.images[indexToUpload] };
        imgSource.image = await getRotatedFileFromImage(imgSource.image.preview, imgSource.rotateDegrees);

        let thumbnailSource = { ...this.state.images[indexToUpload] };
        thumbnailSource.image = await compressImage(thumbnailSource.image.preview, 450, imgSource.rotateDegrees);

        try {
            await this.props.store.userPhotoStore.uploadPhoto(imgSource, thumbnailSource);
        } catch (error) {
            let errorText = `Error uploading ${imgSource.filename}: ${error}`;
            this.setState({errors: [...this.state.errors, errorText]});
            console.log(error);
        }

        return this.uploadAllImages();
    }

    protected renderPhotoDataInput(tagType: TagType) {
        const currentImage = this.state.images[this.state.currentImageFocusIndex];

        return (
            <PhotoDataInput
                tagType={tagType}
                image={currentImage}
                onImageUpdate={image => {
                    const state = [...this.state.images];
                    state[this.state.currentImageFocusIndex] = image;
                    this.setState({ images: state });
                }}
            />
        );
    }

    protected abstract renderEmptyPhotosHeader(): JSX.Element | null;
    protected abstract renderPhotosHeader(): JSX.Element | null;
    protected abstract renderPhotosBottomBar(): JSX.Element | null;
    protected abstract onFinishedUploadedPhotosCallback(): Promise<void>;

    protected rotatePhoto(): void {
        const images = [...this.state.images];
        const photo = images[this.state.currentImageFocusIndex];
        photo.rotateDegrees = getNextRotation(photo.rotateDegrees);
        images[this.state.currentImageFocusIndex] = photo;
        this.setState({ images });
    }

    protected renderContent(): React.ReactFragment {
        return [
            this.state.images.length === 0 && (
                <FullContainer key="empty-photos" style={styles.container}>
                    {this.renderEmptyPhotosHeader()}
                    <div style={styles.uploadContainer}>
                        <Dropzone
                            ref={(ref: any) => {
                                this.dropzoneRef = ref;
                            }}
                            style={styles.innerDropZone}
                            onDrop={(files: any) => {
                                this.onDrop(files);
                            }}
                            accept={ACCEPT_IMAGE_TYPE}
                            disableClick={true}
                            onDropRejected={rejectedFiles => {
                                rejectedFiles.forEach(file => {
                                    alert(`${file.name} ${ui.NOT_SUPPORTED}`);
                                });
                            }}
                        >
                            <div style={styles.relative}>
                                <img src={UploadPhotoIcon} style={styles.uploadIcon} />
                                <InformationIcon link={FRESHDESK.OPTIONS_FOR_UPLOADING_PHOTOS} style={styles.iconLink} />
                            </div>
                            <p style={styles.actionText}>{ui.ACTION_TEXT}</p>
                            <p style={styles.subText}>{ui.SUB_TEXT}</p>
                            <CommonActionButton
                                buttonContent={ui.BUTTON_TEXT}
                                onClick={() => this.dropzoneRef.open()}
                                containerStyle={styles.button}
                                fontStyle={styles.buttonText}
                            />

                            {this.state.errors.length > 0 && (
                                <Errors errors={this.state.errors} />
                            )}

                        </Dropzone>
                    </div>
                </FullContainer>
            ),
            this.state.images.length !== 0 && (
                <FullContainer key="photos" style={styles.container}>
                    <div style={styles.addPhotosHeader}>
                        {this.renderPhotosHeader()}
                        <div style={styles.addPhotosContainer}>
                            <div style={styles.addPhotosLeft}>
                                <Dropzone
                                    ref={(ref: any) => {
                                        this.dropzoneRef = ref;
                                    }}
                                    onDrop={(files: any) => {
                                        this.onDrop(files);
                                    }}
                                    accept={ACCEPT_IMAGE_TYPE}
                                    disableClick={true}
                                    style={styles.addPhotosUploader}
                                    className="customScrollBar"
                                >
                                    <div style={styles.wrapContainer}>
                                        {this.state.images.map((image, index) => {
                                            return (
                                                <div
                                                    key={index}
                                                    style={{
                                                        ...styles.imageContainer,
                                                        ...(index === this.state.currentImageFocusIndex
                                                            ? styles.selectedImageContainer
                                                            : {}),
                                                    }}
                                                >
                                                    <img
                                                        src={image.image.preview}
                                                        style={{
                                                            ...styles.uploadedImage,
                                                            transform: `rotate(${image.rotateDegrees}deg)`,
                                                        }}
                                                        onClick={() => this.setState({ currentImageFocusIndex: index })}
                                                    />
                                                </div>
                                            );
                                        })}
                                    </div>
                                </Dropzone>
                                <div style={styles.additionalUploadContainer}>
                                    <img src={UploadPhotoIcon} style={styles.uploadPhotoIcon} />
                                    <div style={styles.dropPhotoTextContainer}>
                                        <p style={styles.dropPhotoText}>{`${ui.DROP_MORE} (${
                                            this.state.images.length
                                        } / ${MAX_PHOTOS_PER_UPLOAD})`}</p>
                                        <p style={styles.uploadSubText}>{ui.SUB_TEXT}</p>
                                    </div>
                                    <CommonActionButton
                                        containerStyle={styles.chooseFilesButton}
                                        fontStyle={styles.smallFont}
                                        buttonContent={ui.CHOOSE_FILES}
                                        onClick={() => this.dropzoneRef.open()}
                                    />
                                </div>
                            </div>
                            <div style={styles.addPhotosRight}>
                                <div style={styles.addPhotosInputsContainer}>
                                    {this.renderPhotoDataInput(TagType.LOCATION)}
                                    {this.renderPhotoDataInput(TagType.DATE)}
                                    {this.renderPhotoDataInput(TagType.FILENAME)}
                                    <div style={styles.rotateAndRemoveContainer}>
                                        <CommonActionButton onClick={() => this.rotatePhoto()} buttonContent={ui.ROTATE} />
                                        <CommonActionButton
                                            onClick={() => this.removeCurrentImage()}
                                            buttonContent={ui.REMOVE}
                                            containerStyle={styles.deleteButton}
                                        />
                                    </div>

                                    {this.state.errors.length > 0 && !this.state.showUploadProgress && (
                                        <Errors errors={this.state.errors} />
                                    )}

                                </div>
                            </div>
                        </div>
                    </div>
                    {this.renderPhotosBottomBar()}
                    {this.state.showUploadProgress && (
                        <div style={styles.uploadingPhotosContainer}>
                            <p style={styles.uploadingPhotosText}>{ui.UPLOADING_PHOTOS}</p>
                            <p style={styles.uploadingPhotosCount}>{`${this.state.indexOfImageToUpload} out of ${
                                this.state.images.length
                            }`}</p>

                            {this.state.errors.length > 0 && (
                                <React.Fragment>
                                    <Errors
                                        errors={this.state.errors}
                                    />
                                    {this.state.showUploadErrorConfirmButton && (
                                        <CommonActionButton
                                            buttonContent="OK"
                                            onClick={() => this.onFinishedUploadedPhotosCallback()}
                                            containerStyle={styles.button}
                                            fontStyle={styles.buttonText}
                                        />
                                    )}
                                </React.Fragment>
                            )}
                        </div>
                    )}
                    ,
                    {this.state.showExtraPhotoNumber && (
                        <ExitableAlert
                            headerText={ui.CANNOT_UPLOAD_MORE(this.state.showExtraPhotoNumber)}
                            confirmText={ui.OK}
                            onConfirmClick={() => this.setState({ showExtraPhotoNumber: undefined })}
                        />
                    )}
                </FullContainer>
            ),
            this.state.possibleDuplicates.length !== 0 && this.renderDuplicatePrompt(),
        ];
    }
}

export class Exif {
    public static getUpdatedImageFiles(files: Array<ImageFile>): Promise<Array<IUploadedPhoto>> {
        return Promise.all(
            files.map(async (file: any) => {
                let dateString = '';
                let rotateDegrees = 0;
                let imageError = null;

                try {
                    const exif = await Exif.getDateAndRotation(file);
                    dateString = exif.date || dateString;
                    rotateDegrees = exif.rotateDegrees || rotateDegrees;
                } catch (error) {
                    console.log(error);
                    imageError = error.toString();
                }

                if (dateString === '' && file.lastModifiedDate) {
                    dateString = moment(file.lastModifiedDate).format(PHOTO_DATE_FORMAT);
                }

                return {
                    image: file,
                    location: '',
                    date: dateString!,
                    filename: file.name,
                    rotateDegrees,
                    error: imageError,
                };
            }),
        );
    }

    public static getDateAndRotation(
        file: ImageFile,
    ): Promise<{ date: string | undefined; rotateDegrees: number | undefined }> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                if (!reader.result || typeof reader.result === 'string') {
                    return;
                }
                try {
                    const tags = ExifReader.load(reader.result);
                    const { DateTime, DateTimeDigitized, DateTimeOriginal, Orientation } = tags;
                    const rotateDegrees = Orientation ? RotationByExifOrientation[Orientation.value] : undefined;
                    const date = this.convertDateAttributeToDate(DateTimeOriginal || DateTimeDigitized || DateTime);
                    resolve({ date, rotateDegrees });
                } catch (error) {
                    reject(error);
                }
            };
            reader.readAsArrayBuffer(file.slice(0, 128 * 1024));
        });
    }

    private static convertDateAttributeToDate(attribute: ExifDateAttribute | undefined): string | undefined {
        if (!attribute) {
            return undefined;
        }
        const dateString = attribute.value ? attribute.value[0] || attribute.description : attribute.description;
        if (!dateString) {
            return undefined;
        }

        return moment(dateString, 'YYYY:MM:DD hh:mm:ss').format(PHOTO_DATE_FORMAT);
    }
}

const RotationByExifOrientation: {[key: number]: number} = {
    1: 0,
    2: 0, // Update once we can flip images
    3: 180,
    4: 180, // Update once we can flip images
    5: 90, // Update once we can flip images
    6: 90,
    7: 270, // Update once we can flip images
    8: 270,
};



// WEBPACK FOOTER //
// ./src/Pages/AddPhotosPage/AbstractAddPhotosPage.tsx